// ATOMS: BLOG SEARCH
class BlogListing extends HTMLElement {
  constructor() {
    super();
    this.results = null;
    this.tabs = null;
    this.pagination = null;
    this.searchForm = null;
    this.searchInput = null;
    this.searchUrl = "";
    this.originalResults = "";
    this.debounceTimer = null;
    this.controller = null;
    this.onClick = this.onClick.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onSearchSubmit = this.onSearchSubmit.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onNativeSearch = this.onNativeSearch.bind(this);
  }

  connectedCallback() {
    if (this.dataset.blogInit === "true") return;
    this.dataset.blogInit = "true";

    this.results = this.querySelector("[data-blog-results]");
    this.tabs = this.querySelector("[data-blog-tabs]");
    this.pagination = this.querySelector("[data-blog-pagination]");
    this.searchForm = this.querySelector("[data-blog-search]");
    this.searchInput = this.querySelector("#BlogSearchInput");
    this.searchUrl = this.searchForm?.dataset.searchUrl || "";
    this.originalResults = this.results?.innerHTML || "";

    if (!this.results) return;

    this.addEventListener("click", this.onClick);
    this.searchForm?.addEventListener("submit", this.onSearchSubmit);
    this.searchInput?.addEventListener("input", this.onSearchInput);
    this.searchInput?.addEventListener("search", this.onNativeSearch);
    window.addEventListener("popstate", this.onPopState);
    this.scrollActiveTabIntoView();
    this._initDragScroll();
    this.loadSearchFromUrl();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    this.searchForm?.removeEventListener("submit", this.onSearchSubmit);
    this.searchInput?.removeEventListener("input", this.onSearchInput);
    this.searchInput?.removeEventListener("search", this.onNativeSearch);
    window.removeEventListener("popstate", this.onPopState);
    clearTimeout(this.debounceTimer);
    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
  }

  onPopState() {
    if (new URLSearchParams(location.search).get("q")) {
      this.loadSearchFromUrl();
      return;
    }
    this.load(location.href, { push: false });
  }

  onSearchSubmit(event) {
    event.preventDefault();
    this.loadSearchResults();
  }

  onSearchInput(event) {
    clearTimeout(this.debounceTimer);
    const term = event.target.value;
    this.debounceTimer = setTimeout(() => this.loadSearchResults(term), 300);
  }

  // ATOMS: El icono nativo de limpiar de un input search solo dispara el evento search.
  onNativeSearch(event) {
    if (event.target.value) return;
    clearTimeout(this.debounceTimer);
    this.loadSearchResults("");
  }

  onClick(event) {
    const tabControl = event.target.closest("[data-blog-tabs-prev], [data-blog-tabs-next]");
    if (tabControl && tabControl.closest("[data-blog-tabs]")) {
      event.preventDefault();
      this.scrollTabs(tabControl.hasAttribute("data-blog-tabs-prev") ? -1 : 1);
      return;
    }

    const link = event.target.closest("a");
    if (!link) return;
    if (!link.href) return;
    if (!link.closest("[data-blog-tabs]") && !link.closest("[data-blog-pagination]")) {
      return;
    }
    if (!this.shouldHandleLink(event, link)) return;

    event.preventDefault();
    this.load(link.href, { push: true });
  }

  shouldHandleLink(event, link) {
    if (event.defaultPrevented) return false;
    if (event.button !== 0) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return false;
    }
    if (link.target && link.target !== "_self") return false;
    if (link.hasAttribute("download")) return false;
    if (link.origin !== location.origin) return false;
    return true;
  }

  setLoading(isLoading) {
    if (!this.results) return;
    this.results.classList.toggle("opacity-60", isLoading);
    this.results.classList.toggle("pointer-events-none", isLoading);
    this.results.setAttribute("aria-busy", isLoading ? "true" : "false");
    this.dataset.loading = isLoading ? "true" : "false";
  }

  updateSearchUrl(term) {
    const params = new URLSearchParams();
    if (term) params.set("q", term);
    const queryString = params.toString();
    history.replaceState(null, "", queryString ? `${location.pathname}?${queryString}` : location.pathname);
  }

  restoreOriginalResults() {
    if (!this.results) return;
    this.results.innerHTML = this.originalResults;
    this.pagination?.classList.remove("hidden");
  }

  // ATOMS: Recupera el listado original del blog al cancelar una búsqueda.
  reloadBlogResults() {
    const url = new URL(location.href);
    url.searchParams.delete("q");
    history.replaceState(null, "", `${url.pathname}${url.search}`);
    this.load(url.toString(), { push: false });
  }

  loadSearchFromUrl() {
    const params = new URLSearchParams(location.search);
    const term = params.get("q") || "";

    if (this.searchInput) this.searchInput.value = term;
    if (term) {
      this.loadSearchResults(term, false);
    }
  }

  // ATOMS: Busca artículos y reemplaza el listado sin recargar la página.
  async loadSearchResults(term = this.searchInput?.value || "", updateUrl = true) {
    const query = term.trim();
    const searchUrl = this.searchUrl || this.searchForm?.action;
    if (!searchUrl || !this.results) return;

    if (this.controller) {
      this.controller.abort();
    }
    if (query.length < 2) {
      this.reloadBlogResults();
      return;
    }

    const url = searchUrl
      + "?q=" + encodeURIComponent(query)
      + "&type=article"
      + "&options[prefix]=last"
      + "&view=ajax";

    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);

    try {
      // ATOMS: Evita reutilizar una respuesta AJAX en caché con imágenes diferidas antiguas.
      const response = await fetch(url, { signal: controller.signal, cache: "no-store" });
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status} ${response.statusText}`);
      }
      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      // ATOMS: Ignora respuestas que llegaron después de una cancelación o una búsqueda nueva.
      if (this.controller !== controller) return;
      const sourceResults = doc.querySelector("[data-search-ajax-results]");
      if (!sourceResults) return;

      this.results.innerHTML = sourceResults.innerHTML;
      this.pagination?.classList.add("hidden");
      if (updateUrl) this.updateSearchUrl(query);

      if (typeof webvista?.initLazyImageAnimation === "function") {
        webvista.initLazyImageAnimation(this.results);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error(error);
      this.restoreOriginalResults();
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.setLoading(false);
      }
    }
  }

  async load(url, { push }) {
    if (!this.results) return;
    if (this.controller) {
      this.controller.abort();
    }
    const controller = new AbortController();
    this.controller = controller;
    this.setLoading(true);

    try {
      const doc = await webvista.fetchHtml(url, controller.signal);
      const sourceResults = doc.querySelector("[data-blog-results]");
      if (!sourceResults) return;

      this.results.innerHTML = sourceResults.innerHTML;
      this.pagination = this.querySelector("[data-blog-pagination]");
      // ATOMS: Restablece la paginación al cargar el listado normal después de cancelar una búsqueda.
      this.pagination?.classList.remove("hidden");

      const sourceTabs = doc.querySelector("[data-blog-tabs]");
      if (sourceTabs && this.tabs) {
        this.tabs.innerHTML = sourceTabs.innerHTML;
        this._initDragScroll();
        this.scrollActiveTabIntoView();
      }

      if (typeof webvista?.initLazyImageAnimation === "function") {
        webvista.initLazyImageAnimation(this.results);
      }

      this.scrollToTop();

      if (push) {
        history.pushState({ url }, "", url);
      }
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error(error);
    } finally {
      if (this.controller === controller) {
        this.controller = null;
        this.setLoading(false);
      }
    }
  }

  scrollActiveTabIntoView() {
    if (!this.tabs) return;
    const container = this.tabs.querySelector("[data-blog-tabs-scroll]");
    if (!container) return;
    const active = container.querySelector("[data-blog-tab-active]");
    if (!active) return;
    const scrollLeft =
      active.offsetLeft - container.offsetWidth / 2 + active.offsetWidth / 2;
    container.scrollTo({ left: Math.max(0, scrollLeft), behavior: "smooth" });
  }

  scrollTabs(direction) {
    const container = this.tabs?.querySelector("[data-blog-tabs-scroll]");
    if (!container) return;
    const distance = Math.max(container.clientWidth * 0.75, 160);
    container.scrollBy({ left: direction * distance, behavior: "smooth" });
  }

  updateTabControls(container) {
    if (!this.tabs || !container) return;
    const previous = this.tabs.querySelector("[data-blog-tabs-prev]");
    const next = this.tabs.querySelector("[data-blog-tabs-next]");
    const maxScroll = container.scrollWidth - container.clientWidth;
    previous?.toggleAttribute("disabled", container.scrollLeft <= 1);
    next?.toggleAttribute("disabled", container.scrollLeft >= maxScroll - 1);
  }

  scrollToTop() {
    const target = this.querySelector("[data-blog-list]") || this;
    const offset = window.innerHeight * 0.4;
    const targetTop =
      target.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
  }

  _initDragScroll() {
    const el = this.tabs?.querySelector("[data-blog-tabs-scroll]");
    if (!el) return;
    el.onscroll = () => this.updateTabControls(el);
    this.updateTabControls(el);

    let isDragging = false;
    let hasMoved = false;
    let startX = 0;
    let startScroll = 0;
    let pointerId = null;
    const threshold = 6;

    const onPointerDown = (e) => {
      // Skip touch — native scroll is smoother
      if (e.pointerType === "touch" || e.pointerType === "pen") return;
      if (e.button !== 0) return;
      isDragging = true;
      hasMoved = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      pointerId = e.pointerId;
      el.addEventListener("pointermove", onPointerMove);
      el.addEventListener("pointerup", onPointerUp);
      el.addEventListener("pointercancel", onPointerUp);
    };

    const onPointerMove = (e) => {
      if (!isDragging) return;
      const dx = e.clientX - startX;
      if (!hasMoved && Math.abs(dx) < threshold) return;
      if (!hasMoved) {
        hasMoved = true;
        try { el.setPointerCapture(pointerId); } catch {}
        el.classList.add("select-none", "cursor-grabbing");
        el.classList.remove("cursor-grab");
      }
      e.preventDefault();
      el.scrollLeft = startScroll - dx;
    };

    const onPointerUp = () => {
      if (!isDragging) return;
      isDragging = false;
      if (pointerId !== null) {
        try { el.releasePointerCapture(pointerId); } catch {}
      }
      pointerId = null;
      el.classList.remove("select-none", "cursor-grabbing");
      el.classList.add("cursor-grab");
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };

    el.classList.add("cursor-grab");
    el.addEventListener("pointerdown", onPointerDown);

    // Prevent click navigation when dragging
    el.addEventListener("click", (e) => {
      if (hasMoved) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }
}

customElements.define("blog-listing", BlogListing);
